import { Box, Stack } from '@mui/material'
import { reservationChangeRestriction, yesNo } from '../../containers/common/constant/classification'
import { useAction } from '../../containers/facilityReservationSelection/facilityReservationSelectionService'
import { translate } from '../../i18n'
import { AnnotationLabel } from '../components/common/annotationLabel'
import { GContainer, GItem } from '../components/common/grids'
import {
  AvailabilityCalendar,
  calendarChangeRestriction,
  CalendarChangeRestrictionType,
} from '../components/common/inputs/availabilityCalendar'
import { Form } from '../components/common/inputs/form'
import ChildSelection from '../components/common/reservationFlow/selection/childSelection'
import DiagonosisSelection from '../components/common/reservationFlow/selection/diagonosisSelection'
import BottomButtons from '../components/common/reservationFlow/selection/bottomButtons'
import DateSelection from '../components/common/reservationFlow/selection/dateSelection'
import FacilityLinkedName from '../components/common/reservationFlow/selection/facilityLinkedName'
import FacilityNoteCard from '../components/common/reservationFlow/selection/facilityNote'
import ReservationStepBar, { RESERVATION_STEPS } from '../components/common/reservationFlow/reservationStepBar'
import UseReasonSelection from '../components/common/reservationFlow/selection/useReasonSelection'
import { AttentionLabel } from '../components/common/attentionLabel'

/** 施設の予約変更制限値とカレンダーコントロールの変更制限値のマッピング */
const reservationChangeRestrictionToCalendarChangeableMap: Readonly<Record<string, CalendarChangeRestrictionType>> = {
  [reservationChangeRestriction.allowChangeDatetime]: calendarChangeRestriction.datetime,
  [reservationChangeRestriction.allowChangeTimeOnly]: calendarChangeRestriction.time,
  [reservationChangeRestriction.notAllowChangeDatetime]: calendarChangeRestriction.none,
} as const

export const FacilityReservationSelection = () => {
  const {
    state,
    previousRange,
    formMethods,
    onSubmit,
    changeSelectedChildIds,
    changeDropDownSelectedChildId,
    changeDate,
    purposeOfUse,
    useReasonDetailIrregular,
    useReasonDetailEmg,
    useReasonDetailRefresh,
    onChangeUseReason,
    handleChangeFirst,
    handleChangeSecond,
    multiChildReservationAvailabilityFlag,
    isShowFirstOTher,
    isShowSecondOTher
  } = useAction()

  if (!state) {
    return null
  }

  return (
    <Form onSubmit={onSubmit} formMethods={formMethods}>
      <Stack spacing={1}>
        <GContainer>
          <GItem md={3}></GItem>
          <GItem xs={12} md={6}>
            <Box>
              <GContainer rowSpacing={1}>
                <GItem xs={12}>
                  <ReservationStepBar activeStepValue={RESERVATION_STEPS.SELECTION} />
                </GItem>
                <GItem xs={12}>
                  <FacilityLinkedName
                    facilityId={state.facilityId}
                    facilityName={state.facility.facilityName}
                  />
                </GItem>
                <GItem xs={12}>
                  <Stack spacing={1}>
                    <ChildSelection
                      changeSelectedChildIds={changeSelectedChildIds}
                      changeDropDownSelectedChildId={changeDropDownSelectedChildId}
                      childs={state.children}
                      control={formMethods.control}
                      multiChildReservationAvailabilityFlag={multiChildReservationAvailabilityFlag}
                      checkedChildren={state.selectedChildIds}
                      isSelectable={previousRange ? false : true}
                    />
                    {
                      state.commonFlag === yesNo.no
                      && Object.values(state.useReasonOptions).some((usage) => usage)
                      && (
                        <UseReasonSelection
                          useReason={state.useReason}
                          useReasonOptions={state.useReasonOptions}
                          facility={state.facility}
                          purposeOfUse={purposeOfUse ?? ''}
                          useReasonDetailIrregular={useReasonDetailIrregular ?? ''}
                          useReasonDetailEmg={useReasonDetailEmg ?? ''}
                          useReasonDetailRefresh={useReasonDetailRefresh ?? ''}
                          onChange={onChangeUseReason}
                          setValue={formMethods.setValue}
                        />
                      )
                    }
                    {
                      state.isolationFlag && (
                        <>
                          <DiagonosisSelection
                            diagonosisDecodeMst={state.diagonosisDecodeMst}
                            changeDropDownSelectedDiagonosis={handleChangeFirst}
                            state={state}
                            name={'first'}
                            showOtherFlag={isShowFirstOTher}
                            isrequired={true}
                          />
                          <DiagonosisSelection
                            diagonosisDecodeMst={state.diagonosisDecodeMst}
                            changeDropDownSelectedDiagonosis={handleChangeSecond}
                            state={state}
                            name={'second'}
                            showOtherFlag={isShowSecondOTher}
                            isrequired={false}
                          />
                        </>
                      )
                    }
                    <DateSelection activeDate={state.activeDate} onChange={changeDate} />
                  </Stack>
                </GItem>
                <GItem xs={12}>
                  {
                    state.availabilities.length > 0 && (
                      <Stack spacing={1}>
                        <GItem xs={12}>
                          <Stack spacing={2}>
                            <AnnotationLabel>{translate(state.usageGuideStr)}</AnnotationLabel>
                            <AvailabilityCalendar
                              name="usageDesiredDatetimes"
                              label={translate('facilityReservationSelection.label.availabilityCalendar')}
                              required
                              baseDate={state.activeDate}
                              previousRange={previousRange}
                              receptionTimePattern={state.facility.receptionTimePattern}
                              availabilities={state.availabilities}
                              initialValidationAvailabilities={state.initialValidationAvailabilities}
                              isMultiple={previousRange == null && state.facility.citizenBulkReservationAcceptFlag === yesNo.yes}
                              onChangeBaseDate={changeDate}
                              control={formMethods.control}
                              changeRestriction={
                                reservationChangeRestrictionToCalendarChangeableMap[state.facility.reservationChangeRestriction]
                              }
                            />
                          </Stack>
                        </GItem>
                        <GItem xs={12}>
                          {
                            state.note && (
                              <FacilityNoteCard>{state.note}</FacilityNoteCard>
                            )
                          }
                        </GItem>
                        <GItem xs={12}>
                          {
                            state.lotteryNote && (
                              <FacilityNoteCard>{state.lotteryNote}</FacilityNoteCard>
                            )
                          }
                        </GItem>
                        {
                          state.commonFlag === yesNo.yes 
                          && Object.values(state.useReasonOptions).some((usage) => usage)
                          && (
                            <GItem xs={12}>
                              <UseReasonSelection
                                useReason={state.useReason}
                                useReasonOptions={state.useReasonOptions}
                                facility={state.facility}
                                purposeOfUse={purposeOfUse?? ''}
                                useReasonDetailIrregular={useReasonDetailIrregular?? ''}
                                useReasonDetailEmg={useReasonDetailEmg?? ''}
                                useReasonDetailRefresh={useReasonDetailRefresh?? ''}
                                onChange={onChangeUseReason}
                                setValue={formMethods.setValue}
                              />
                            </GItem>
                          )
                        }
                      </Stack>                
                    )
                  }
                </GItem>
              </GContainer>
            </Box>
            {
              state.availabilities.length > 0 && (
                <AttentionLabel>{translate('facilityReservationSelection.label.annotation')}</AttentionLabel>
              )
            }
          </GItem>
          <GItem md={3}></GItem>
        </GContainer>
        <BottomButtons hideNextSpepBtn={state.availabilities.length === 0}/>
      </Stack>
    </Form>
  )
}
